import $ from 'jquery';
import en from './locales/en';
import ru from './locales/ru';

const LOCALES = { en, ru };
const DEFAULT_LOCALE = 'en';

let currentLocale = DEFAULT_LOCALE;
let messages = LOCALES[DEFAULT_LOCALE];

/**
 * Resolve nested translation key (e.g. "menu.settings")
 *
 * @param {object} source
 * @param {string} key
 * @return {string|undefined}
 */
function resolveKey(source, key) {
    return key.split('.').reduce((value, part) => {
        if (value == null) {
            return undefined;
        }

        return value[part];
    }, source);
}

/**
 * @module I18n
 */
const I18n = {
    /**
     * @method init
     * @param {string} locale
     */
    init(locale) {
        this.setLocale(locale || DEFAULT_LOCALE, { silent: true });
    },

    /**
     * @method getLocale
     * @return {string}
     */
    getLocale() {
        return currentLocale;
    },

    /**
     * @method setLocale
     * @param {string} locale
     * @param {object} [options]
     */
    setLocale(locale, options = {}) {
        const nextLocale = LOCALES[locale] ? locale : DEFAULT_LOCALE;

        currentLocale = nextLocale;
        messages = LOCALES[nextLocale];

        if (!options.silent) {
            this.applyToDocument(document.body);
        }
    },

    /**
     * @method t
     * @param {string} key
     * @param {string} [fallback]
     * @return {string}
     */
    t(key, fallback = key) {
        const value = resolveKey(messages, key);

        return typeof value === 'string' ? value : fallback;
    },

    /**
     * Apply data-i18n* attributes within root element
     *
     * @method applyToDocument
     * @param {Element|JQuery} root
     */
    applyToDocument(root) {
        const $root = root.jquery ? root : $(root);

        $root.find('[data-i18n]').addBack('[data-i18n]').each(function applyText() {
            const key = $(this).data('i18n');

            $(this).text(I18n.t(key));
        });

        $root.find('[data-i18n-html]').addBack('[data-i18n-html]').each(function applyHtml() {
            const key = $(this).data('i18n-html');

            $(this).html(I18n.t(key));
        });

        $root.find('[data-i18n-title]').addBack('[data-i18n-title]').each(function applyTitle() {
            const key = $(this).data('i18n-title');

            $(this).attr('title', I18n.t(key));
        });

        $root.find('[data-i18n-placeholder]').addBack('[data-i18n-placeholder]').each(function applyPlaceholder() {
            const key = $(this).data('i18n-placeholder');

            $(this).attr('placeholder', I18n.t(key));
        });
    },

    /**
     * @method getCommandPlaceholder
     * @param {boolean} isScopeMode
     * @return {string}
     */
    getCommandPlaceholder(isScopeMode) {
        return isScopeMode
            ? this.t('command.scopePlaceholder')
            : this.t('command.aircraftPlaceholder');
    },

    /**
     * @method getAutocompletePlaceholder
     * @param {'command'|'param'} type
     * @return {string}
     */
    getAutocompletePlaceholder(type) {
        return type === 'param'
            ? this.t('autocomplete.inputParam')
            : this.t('autocomplete.inputCommand');
    },

    /**
     * @method getSettingDescription
     * @param {string} optionName
     * @param {string} fallback
     * @return {string}
     */
    getSettingDescription(optionName, fallback) {
        return this.t(`settings.options.${optionName}.description`, fallback);
    },

    /**
     * @method getSettingOptionLabel
     * @param {string} optionName
     * @param {string|number} value
     * @param {string} fallback
     * @return {string}
     */
    getSettingOptionLabel(optionName, value, fallback) {
        return this.t(`settings.options.${optionName}.options.${value}`, fallback);
    },

    /**
     * @method getFlightCategoryLabel
     * @param {string} category
     * @return {string}
     */
    getFlightCategoryLabel(category) {
        return this.t(`traffic.categories.${category}`, category);
    }
};

export default I18n;
